// src/…/MenuSearchMateriais.js
import { useEffect, useMemo, useState } from "react";

import api, { handleApi } from "pages/api/api";
import { useAlert } from "pages/context/AlertContext";
import { useAuth } from "pages/context/AuthContext";
import Draggable from "react-draggable";

import Paper from "@mui/material/Paper";
import { useTheme } from "@mui/material/styles";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";

import MDButton from "components/MDButton";

import DefaultLineChart from "examples/Charts/LineCharts/DefaultLineChart";
import { useTable, useFilters } from "react-table";

const mesesLabels = [
  "Jan",
  "Fev",
  "Mar",
  "Abr",
  "Mai",
  "Jun",
  "Jul",
  "Ago",
  "Set",
  "Out",
  "Nov",
  "Dez",
];

function PaperComponent(props) {
  return (
    <Draggable handle=".draggable-dialog-title" cancel={'[class*="MuiDialogContent-root"]'}>
      <Paper {...props} />
    </Draggable>
  );
}

export default function MenuSearchMateriais({ open, onClose, material, centrosSelecionados = [] }) {
  const { showAlert } = useAlert();
  const { temFuncao, userData } = useAuth();
  const theme = useTheme();

  const colorMap = {
    warning: theme.palette.warning.main,
    primary: theme.palette.primary.main,
    secondary: theme.palette.secondary.main,
  };

  const podeVer = temFuncao("Administração") || temFuncao("Informática") || temFuncao("Tesouraria");

  const podeGerirStocks = temFuncao("Gestão Stocks");
  const podeAlterarStock = podeVer || podeGerirStocks;
  const idUtilizadorAuth = Number(userData?.id || 0);

  const [stocksLoading, setStocksLoading] = useState(false);
  const [stocks, setStocks] = useState([]);

  const [stockDialogOpen, setStockDialogOpen] = useState(false);
  const [stockSelecionado, setStockSelecionado] = useState(null);
  const [stockForm, setStockForm] = useState({
    qtd_verificada: "",
    localizacao: "",
    obs: "",
  });

  const [vendasLoading, setVendasLoading] = useState(false);
  const [vendas, setVendas] = useState([]);

  const [vendasLinhasLoading, setVendasLinhasLoading] = useState(false);
  const [vendasLinhas, setVendasLinhas] = useState([]);

  const [comprasLoading, setComprasLoading] = useState(false);
  const [compras, setCompras] = useState([]);

  // preços por material
  const [precosOpen, setPrecosOpen] = useState(false);
  const [precosLoading, setPrecosLoading] = useState(false);
  const [precos, setPrecos] = useState(null);
  const [precosRefMat, setPrecosRefMat] = useState("");

  const [urlImagem, setUrlImagem] = useState("");
  const [fabricanteTitulo, setFabricanteTitulo] = useState("");

  const refMaterialAtual = material ? (material.refmaterial || material.ref || "").trim() : "";

  const formatData = (v) => (v ? String(v).substring(0, 10) : "");

  const formatNum = (v, dec = 2) => {
    if (v === null || v === undefined || v === "") return "";
    const n = Number(v);
    if (!Number.isFinite(n)) return "";
    return n.toLocaleString("pt-PT", {
      minimumFractionDigits: dec,
      maximumFractionDigits: dec,
    });
  };

  const joinValPerc = (val, perc, withEuro = false) => {
    const v = formatNum(val);
    const p = formatNum(perc, 1);
    let s = "";
    if (v) s += `${v}${withEuro ? " €" : ""}`;
    if (p) s += `${s ? " " : ""}(${p}%)`;
    return s;
  };

  const calcularMargem = (precosObj) => {
    if (!precosObj) {
      return { margemValor: null, margemPerc: null, margemTexto: "" };
    }

    const venda = Number(precosObj.zpr0 ?? 0);
    const custo = Number(precosObj.zpvr ?? 0);

    if (!Number.isFinite(venda) || !Number.isFinite(custo) || custo === 0) {
      return { margemValor: null, margemPerc: null, margemTexto: "" };
    }

    const margemValor = venda - custo;
    const margemPerc = (margemValor / custo) * 100;

    return {
      margemValor,
      margemPerc,
      margemTexto: joinValPerc(margemValor, margemPerc, true),
    };
  };

  const abrirPrecosMaterial = async (refMat) => {
    const ref = (refMat || "").trim();
    if (!ref) return;

    setPrecosRefMat(ref);
    setPrecos(null);
    setPrecosOpen(true);
    setPrecosLoading(true);

    const [dados, err] = await handleApi(
      api.post("Materiais/SelMaterialPrecos", { refmaterial: ref })
    );

    setPrecosLoading(false);

    if (err) {
      showAlert(err, "error");
      setPrecos(null);
      return;
    }

    setPrecos(dados); // objeto único
  };

  const abrirAlterarStock = (row) => {
    setStockSelecionado(row);
    setStockForm({
      qtd_verificada: row?.quantidade ?? "",
      localizacao: "",
      obs: "",
    });
    setStockDialogOpen(true);
  };

  const fecharAlterarStock = () => {
    setStockDialogOpen(false);
    setStockSelecionado(null);
    setStockForm({
      qtd_verificada: "",
      localizacao: "",
      obs: "",
    });
  };

  const gravarAlteracaoStock = async () => {
    if (!stockSelecionado || !refMaterialAtual) return;

    const qtd = parseInt(stockForm.qtd_verificada, 10);

    if (!Number.isFinite(qtd) || qtd < 0) {
      showAlert("Indica uma quantidade válida.", "warning");
      return;
    }

    const [res, err, meta] = await handleApi(
      api.post("Stocks/InsStockAlteracao", {
        refmaterial: refMaterialAtual,
        refcentro: stockSelecionado.refcentro,
        qtd_verificada: qtd,
        localizacao: stockForm.localizacao || "",
        obs: stockForm.obs || "",
        idutilizador_registo: idUtilizadorAuth,
      })
    );

    if (err) {
      showAlert(err, "error");
      return;
    }

    showAlert(meta?.message || "Alteração de stock registada.", meta?.severity || "success");

    fecharAlterarStock();

    // força recarregar dados do dialog
    setStocks((prev) =>
      prev.map((s) =>
        s.refcentro === stockSelecionado.refcentro ? { ...s, qtd_verificada: qtd } : s
      )
    );
  };

  // ==================== CARREGAR STOCKS / VENDAS / COMPRAS ====================

  const refcentrosKey = useMemo(() => {
    const arr = Array.isArray(centrosSelecionados)
      ? centrosSelecionados.map((x) => String(x || "").trim()).filter(Boolean)
      : [];

    // ordenar para não depender da ordem (1100|1200 == 1200|1100)
    return arr.sort().join("|");
  }, [centrosSelecionados]);

  useEffect(() => {
    const refcentros = refcentrosKey ? refcentrosKey.split("|") : [];

    let active = true;

    const carregar = async () => {
      if (!open || !material) return;

      if (!material.ref && !material.refmaterial) return;

      const refmaterial = (material.refmaterial || material.ref || "").trim();
      if (!refmaterial) return;

      setUrlImagem("");
      setFabricanteTitulo("");

      const [cabecalho, errCabecalho] = await handleApi(
        api.post("Materiais/SelCabecalhoMaterial", { refmaterial })
      );

      if (!active) return;

      if (errCabecalho) {
        showAlert(errCabecalho, "error");
      } else {
        const fab = (cabecalho?.fabricante || "").toString().trim();

        if (fab) {
          setFabricanteTitulo(fab);
        } else {
          setFabricanteTitulo("Este produto não é dos nossos parceiros atuais.");
        }
      }

      const [imgs, errImg] = await handleApi(
        api.post("Materiais/SelMateriaisImagens", { refs: [refmaterial] })
      );

      if (!active) return;

      if (!errImg && Array.isArray(imgs) && imgs.length > 0) {
        const url = (imgs[0]?.url || "").toString().trim();
        setUrlImagem(url);
      }

      // se não for perfil com acesso total, filtra por vendedor do utilizador
      const refvendedor =
        !podeVer && userData?.refvendedor ? String(userData.refvendedor).trim() : "";

      // reset local
      setStocks([]);
      setCompras([]);
      setVendas([]);
      setVendasLinhas([]);

      setStocksLoading(true);
      setComprasLoading(true);
      setVendasLoading(true);
      setVendasLinhasLoading(true);

      // ---- Stocks ----
      const [rowsStocks, errStocks] = await handleApi(
        api.post("Materiais/SelStocksPorMaterial", { refmaterial, idutilizador: userData?.id || 0 })
      );

      if (!active) return;

      setStocksLoading(false);

      if (errStocks) {
        showAlert(errStocks, "error");
        // mantém o dialog aberto, mas sem dados
        return;
      }

      setStocks(Array.isArray(rowsStocks) ? rowsStocks : []);

      // ---- Vendas ----
      const [rowsVendas, errVendas] = await handleApi(
        api.post("Materiais/SelVendasPorMaterial", { refmaterial, refvendedor, refcentros })
      );

      if (!active) return;

      setVendasLoading(false);

      if (errVendas) {
        showAlert(errVendas, "error");
        setVendas([]);
      } else {
        setVendas(Array.isArray(rowsVendas) ? rowsVendas : []);
      }

      // ---- Vendas (faturas linha a linha) ----
      const [rowsVendasLinhas, errVendasLinhas] = await handleApi(
        api.post("Materiais/SelVendasLinhasPorMaterial", {
          refmaterial,
          refvendedor,
          refcentros,
        })
      );

      if (!active) return;

      setVendasLinhasLoading(false);

      if (errVendasLinhas) {
        showAlert(errVendasLinhas, "error");
        setVendasLinhas([]);
      } else {
        setVendasLinhas(Array.isArray(rowsVendasLinhas) ? rowsVendasLinhas : []);
      }

      // ---- Compras ----
      const [rowsCompras, errCompras] = await handleApi(
        api.post("Materiais/SelComprasPorMaterial", { refmaterial })
      );

      if (!active) return;

      setComprasLoading(false);

      if (errCompras) {
        showAlert(errCompras, "error");
        setCompras([]);
        return;
      }

      setCompras(Array.isArray(rowsCompras) ? rowsCompras : []);
    };

    if (open && material) {
      carregar();
    } else if (!open) {
      // quando se fecha, limpamos estados
      setStocks([]);
      setCompras([]);
      setVendas([]);
      setVendasLinhas([]);

      setVendasLinhasLoading(false);
      setStocksLoading(false);
      setComprasLoading(false);
      setVendasLoading(false);
      setVendasLinhasLoading(false);
    }

    return () => {
      active = false;
    };
  }, [open, material, showAlert, podeVer, userData]);

  // ==================== DERIVAÇÃO VENDAS PARA CHART ====================

  // Meses para o eixo X
  const mesesLabels = [
    "Jan",
    "Fev",
    "Mar",
    "Abr",
    "Mai",
    "Jun",
    "Jul",
    "Ago",
    "Set",
    "Out",
    "Nov",
    "Dez",
  ];

  // 1) Agregar vendas por ano/mês em formato limpo (apenas números)
  const vendasPorAno = useMemo(() => {
    if (!Array.isArray(vendas) || vendas.length === 0) return {};

    const anoAtual = new Date().getFullYear();
    const inicio = anoAtual - 2; // últimos 3 anos: anoAtual, anoAtual-1, anoAtual-2

    const mapa = {};

    for (let ano = inicio; ano <= anoAtual; ano += 1) {
      mapa[ano] = new Array(12).fill(0);
    }

    vendas.forEach((row) => {
      const ano = Number(row.ano);
      const mes = Number(row.mes); // 1..12
      const qtd = Number(row.quantidade ?? row.qtd ?? 0);

      if (!Number.isFinite(ano) || !Number.isFinite(mes) || !Number.isFinite(qtd)) return;
      if (ano < inicio || ano > anoAtual) return;

      const idxMes = mes - 1; // 0..11
      if (idxMes < 0 || idxMes > 11) return;

      // acumular quantidade nesse mês/ano
      mapa[ano][idxMes] += qtd;
    });

    // marcar meses FUTUROS do ano atual como null (para a linha parar no mês corrente)
    const agora = new Date();
    const mesAtualIdx = agora.getMonth(); // 0..11

    if (mapa[anoAtual]) {
      for (let i = mesAtualIdx + 1; i < 12; i += 1) {
        mapa[anoAtual][i] = null;
      }
    }

    return mapa;
  }, [vendas]);

  // 2) Anos efetivamente com dados (ordenados desc) — máx 3
  const anosVendas = useMemo(() => {
    const anos = Object.keys(vendasPorAno)
      .map((a) => Number(a))
      .filter((a) => Number.isFinite(a))
      .sort((a, b) => b - a);

    return anos.slice(0, 3);
  }, [vendasPorAno]);

  // 3) Cortar meses “futuros” (sem dados em nenhum ano)
  const { labelsVendas, datasetsVendas } = useMemo(() => {
    if (anosVendas.length === 0) {
      return { labelsVendas: [], datasetsVendas: [] };
    }

    // saber até que mês há pelo menos um valor > 0 em algum ano
    const temDadosEmMes = new Array(12).fill(false);

    anosVendas.forEach((ano) => {
      const arr = vendasPorAno[ano] || [];
      arr.forEach((valor, idx) => {
        if (Number.isFinite(valor) && valor > 0) {
          temDadosEmMes[idx] = true;
        }
      });
    });

    const lastIdx = temDadosEmMes.lastIndexOf(true);

    if (lastIdx === -1) {
      // não há dados nenhuns
      return { labelsVendas: [], datasetsVendas: [] };
    }

    const labels = mesesLabels.slice(0, lastIdx + 1);

    const datasets = anosVendas.map((ano, index) => {
      const base = vendasPorAno[ano] || new Array(12).fill(0);
      return {
        label: String(ano),
        // cores iguais às que já estavas a usar no restante código
        color: index === 0 ? "warning" : index === 1 ? "primary" : "secondary",
        // null = não desenhar ponto/segmento (usado nos meses futuros do ano atual)
        data: base.slice(0, lastIdx + 1).map((v) => (Number.isFinite(v) ? v : null)),
      };
    });

    return { labelsVendas: labels, datasetsVendas: datasets };
  }, [anosVendas, vendasPorAno]);

  // datasets usados tanto na legenda como no gráfico
  const vendasDatasetsFiltrados = datasetsVendas;

  // 4) Há vendas? (evita renderizar chart vazio)
  const temVendas = useMemo(
    () => datasetsVendas.some((ds) => ds.data.some((v) => Number.isFinite(v) && v > 0)),
    [datasetsVendas]
  );

  // 5) Objeto final para o DefaultLineChart
  const vendasChart = useMemo(
    () =>
      temVendas
        ? {
            labels: labelsVendas,
            datasets: datasetsVendas,
          }
        : null,
    [temVendas, labelsVendas, datasetsVendas]
  );

  // totais por ano (para a legenda: "Qtd. total: ...")
  const vendasTotaisAno = useMemo(() => {
    const mapa = {};
    anosVendas.forEach((ano) => {
      const arr = vendasPorAno[ano] || [];
      const total = arr.reduce((acc, v) => acc + (Number.isFinite(v) ? v : 0), 0);
      mapa[String(ano)] = total;
    });
    return mapa;
  }, [anosVendas, vendasPorAno]);

  // ==================== react-table – VENDAS ====================

  const vendasData = useMemo(
    () =>
      (vendas || []).map((row, idx) => {
        const ano = Number(row.ano);
        const mes = Number(row.mes);
        const mesIdx = isNaN(mes) ? 0 : mes - 1;

        return {
          id: idx,
          ano,
          mes: mes, // número 1-12
          mesDesc: mesesLabels[mesIdx] || "",
          quantidade: row.quantidade,
        };
      }),
    [vendas]
  );

  const vendasColumns = useMemo(
    () => [
      { Header: "Ano", accessor: "ano" },
      { Header: "Mês", accessor: "mesDesc" },
      {
        Header: "Qtd",
        accessor: "quantidade",
        align: "right",
        Cell: ({ value }) =>
          Number(value || 0).toLocaleString("pt-PT", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }),
      },
    ],
    []
  );

  const vendasDefaultColumn = useMemo(
    () => ({
      Filter: DefaultColumnFilter,
    }),
    []
  );

  const {
    getTableProps: getVendasTableProps,
    getTableBodyProps: getVendasBodyProps,
    headerGroups: vendasHeaderGroups,
    rows: vendasRows,
    prepareRow: prepareVendasRow,
  } = useTable(
    {
      columns: vendasColumns,
      data: vendasData,
      defaultColumn: vendasDefaultColumn,
    },
    useFilters
  );

  // ==================== react-table – STOCKS ====================

  const stocksData = useMemo(
    () =>
      (stocks || []).map((row, idx) => ({
        id: idx,
        centro: row.centro || "",
        refcentro: row.refcentro || "",
        quantidade: row.quantidade,
        qtd_verificada: row.qtd_verificada,
        unidade: row.refunidade || "",
      })),
    [stocks]
  );

  const stocksColumns = useMemo(() => {
    const cols = [
      { Header: "Centro", accessor: "centro" },
      { Header: "Ref. centro", accessor: "refcentro", minWidth: 150, maxWidth: 170 },
      { Header: "Qtd oficial", accessor: "quantidade", align: "right", minWidth: 80, maxWidth: 90 },
      {
        Header: "Qtd verificada",
        accessor: "qtd_verificada",
        align: "right",
        minWidth: 90,
        maxWidth: 110,
        Cell: ({ row }) => {
          const v = row.original.qtd_verificada;
          const oficial = row.original.quantidade;

          const diferente = v !== null && v !== undefined && Number(v) !== Number(oficial);

          return (
            <span
              style={{
                color: diferente ? "#d32f2f" : undefined,
                fontWeight: diferente ? 700 : 400,
              }}
            >
              {v !== null && v !== undefined ? parseInt(v, 10) : "—"}
            </span>
          );
        },
      },
      { Header: "Unidade", accessor: "unidade", minWidth: 60, maxWidth: 70 },
    ];

    if (podeAlterarStock) {
      cols.push({
        Header: "Ação",
        id: "acao",
        minWidth: 120,
        maxWidth: 140,
        disableFilters: true,
        Cell: ({ row }) => (
          <MDButton
            color="info"
            variant="outlined"
            size="small"
            onClick={() => abrirAlterarStock(row.original)}
          >
            Alterar
          </MDButton>
        ),
      });
    }

    return cols;
  }, [podeAlterarStock]);

  const stocksDefaultColumn = useMemo(
    () => ({
      Filter: DefaultColumnFilter,
    }),
    []
  );

  const {
    getTableProps: getStocksTableProps,
    getTableBodyProps: getStocksBodyProps,
    headerGroups: stocksHeaderGroups,
    rows: stocksRows,
    prepareRow: prepareStocksRow,
  } = useTable(
    {
      columns: stocksColumns,
      data: stocksData,
      defaultColumn: stocksDefaultColumn,
    },
    useFilters
  );

  // ---------- VENDAS (faturas) ----------

  const vendasLinhasData = useMemo(
    () =>
      (vendasLinhas || []).map((row, idx) => {
        const qtd = Number(row.quantidade);
        const total = Number(row.valortotal);
        let valorUnit = "";
        // if (podeVer && qtd && Number.isFinite(qtd) && Number.isFinite(total)) {
        if (qtd && Number.isFinite(qtd) && Number.isFinite(total)) {
          valorUnit = (total / qtd).toFixed(2);
        }

        console.log(row);

        return {
          id: row.id ?? idx,
          data: formatData(row.faturadata),
          fatura: row.faturanumero || row.faturanum || "",
          qtd: row.quantidade,
          // valorTotal: podeVer ? row.valortotal : "",
          valorTotal: row.valortotal,
          valorUnit,
          cliente: row.cliente || row.pagador || "",
        };
      }),
    [vendasLinhas, podeVer]
  );

  const vendasLinhasColumns = useMemo(() => {
    const cols = [
      { Header: "Data", accessor: "data", minWidth: 90, maxWidth: 90 },
      { Header: "Nº fatura", accessor: "fatura", minWidth: 110, maxWidth: 110 },
      { Header: "Qtd", accessor: "qtd", align: "right", minWidth: 60, maxWidth: 70 },
    ];

    // if (podeVer) {
    cols.push(
      {
        Header: "Valor total",
        accessor: "valorTotal",
        align: "right",
        minWidth: 90,
        maxWidth: 110,
      },
      {
        Header: "Valor unit.",
        accessor: "valorUnit",
        align: "right",
        minWidth: 90,
        maxWidth: 110,
      }
    );
    // }

    cols.push({ Header: "Cliente", accessor: "cliente", minWidth: 180 });

    return cols;
  }, [podeVer]);

  const vendasLinhasDefaultColumn = useMemo(
    () => ({
      Filter: DefaultColumnFilter,
    }),
    []
  );

  const {
    getTableProps: getVendasLinhasTableProps,
    getTableBodyProps: getVendasLinhasBodyProps,
    headerGroups: vendasLinhasHeaderGroups,
    rows: vendasLinhasRows,
    prepareRow: prepareVendasLinhasRow,
  } = useTable(
    {
      columns: vendasLinhasColumns,
      data: vendasLinhasData,
      defaultColumn: vendasLinhasDefaultColumn,
    },
    useFilters
  );

  // ==================== react-table – COMPRAS ====================

  const comprasData = useMemo(
    () =>
      (compras || []).map((row, idx) => {
        const qtd = Number(row.quantidade);
        const total = Number(row.valortotal);
        let valorUnit = "";
        if (podeVer && qtd && Number.isFinite(qtd) && Number.isFinite(total)) {
          valorUnit = (total / qtd).toFixed(2);
        }

        return {
          id: row.id ?? idx,
          data: formatData(row.faturadata),
          fatura: row.faturanumero || "",
          qtd: row.quantidade,
          valorTotal: podeVer ? row.valortotal : "",
          valorUnit,
          fabricante: row.fabricante || "",
        };
      }),
    [compras, podeVer]
  );

  const comprasColumns = useMemo(() => {
    const cols = [
      { Header: "Data", accessor: "data", minWidth: 90, maxWidth: 90 },
      { Header: "Nº fatura", accessor: "fatura", minWidth: 110, maxWidth: 110 },
      { Header: "Qtd", accessor: "qtd", align: "right", minWidth: 60, maxWidth: 70 },
    ];

    if (podeVer) {
      cols.push(
        {
          Header: "Valor total",
          accessor: "valorTotal",
          align: "right",
          minWidth: 90,
          maxWidth: 110,
        },
        {
          Header: "Valor unit.",
          accessor: "valorUnit",
          align: "right",
          minWidth: 90,
          maxWidth: 110,
        }
      );
    }

    cols.push({
      Header: "Fabricante",
      accessor: "fabricante",
      minWidth: 180,
    });

    return cols;
  }, [podeVer]);

  const comprasDefaultColumn = useMemo(
    () => ({
      Filter: DefaultColumnFilter,
    }),
    []
  );

  const {
    getTableProps: getComprasTableProps,
    getTableBodyProps: getComprasBodyProps,
    headerGroups: comprasHeaderGroups,
    rows: comprasRows,
    prepareRow: prepareComprasRow,
  } = useTable(
    {
      columns: comprasColumns,
      data: comprasData,
      defaultColumn: comprasDefaultColumn,
    },
    useFilters
  );

  // ==================== RENDER ====================

  const titulo = material
    ? `Stocks / Vendas – ${material.ref ?? ""}${material.nome ? ` – ${material.nome}` : ""}`
    : "Stocks / Vendas do material";

  const { margemTexto } = calcularMargem(precos);

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperComponent={PaperComponent}>
        <DialogTitle className="draggable-dialog-title" style={{ cursor: "move" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {material?.urlImagem ? (
              <img
                src={material.urlImagem}
                alt=""
                style={{
                  width: 34,
                  height: 34,
                  objectFit: "contain",
                  borderRadius: 6,
                  cursor: "pointer",
                }}
                onClick={() => window.open(material.urlImagem, "_blank", "noopener,noreferrer")}
              />
            ) : null}

            <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
              <span>{titulo}</span>

              {fabricanteTitulo ? (
                <span
                  style={{
                    fontSize: 12,
                    color: "#7b809a",
                    marginTop: 2,
                    fontStyle: "italic",
                  }}
                >
                  {fabricanteTitulo}
                </span>
              ) : null}
            </div>
          </div>
        </DialogTitle>

        <DialogContent dividers>
          {/* ================== STOCKS ================== */}
          <h6 style={{ marginTop: 0, marginBottom: 8, fontSize: 13, color: "#7b809a" }}>
            Stocks por centro
          </h6>

          {stocksLoading ? (
            <div
              style={{
                padding: "16px 0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress size={24} />
            </div>
          ) : stocksData.length === 0 ? (
            <div style={{ fontSize: 13, color: "#555", marginBottom: 16 }}>
              Não há stocks registados para este material.
            </div>
          ) : (
            <div style={{ maxHeight: 220, overflowY: "auto", marginBottom: 20 }}>
              <table
                {...getStocksTableProps()}
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: 13,
                }}
              >
                <thead>
                  {stocksHeaderGroups.map((hg) => (
                    <tr {...hg.getHeaderGroupProps()}>
                      {hg.headers.map((col) => {
                        const widthStyle =
                          col.minWidth || col.maxWidth
                            ? {
                                ...(col.minWidth && {
                                  width: col.minWidth,
                                  minWidth: col.minWidth,
                                }),
                                ...(col.maxWidth && {
                                  maxWidth: col.maxWidth,
                                }),
                              }
                            : {};

                        return (
                          <th
                            {...col.getHeaderProps()}
                            style={{
                              ...thStyle,
                              ...widthStyle,
                              textAlign: col.align || "left",
                              position: "sticky",
                              top: 0,
                              zIndex: 2,
                              backgroundColor: "#f8f9fa",
                            }}
                          >
                            <div>{col.render("Header")}</div>
                            {col.canFilter && (
                              <div style={{ marginTop: 4 }}>{col.render("Filter")}</div>
                            )}
                          </th>
                        );
                      })}
                    </tr>
                  ))}
                </thead>

                <tbody {...getStocksBodyProps()}>
                  {stocksRows.map((row) => {
                    prepareStocksRow(row);
                    return (
                      <tr {...row.getRowProps()}>
                        {row.cells.map((cell) => {
                          const col = cell.column;
                          const widthStyle =
                            col.minWidth || col.maxWidth
                              ? {
                                  ...(col.minWidth && {
                                    width: col.minWidth,
                                    minWidth: col.minWidth,
                                  }),
                                  ...(col.maxWidth && { maxWidth: col.maxWidth }),
                                }
                              : {};

                          return (
                            <td
                              {...cell.getCellProps()}
                              style={{
                                ...tdStyle,
                                ...widthStyle,
                                textAlign: col.align || "left",
                              }}
                            >
                              {cell.render("Cell")}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {refMaterialAtual && (
            <div
              style={{
                marginTop: 4,
                marginBottom: 4,
                display: "flex",
                justifyContent: "flex-start",
              }}
            >
              <MDButton
                color="info"
                variant="gradient"
                size="small"
                onClick={() => abrirPrecosMaterial(refMaterialAtual)}
              >
                VER PREÇOS
              </MDButton>
            </div>
          )}

          {/* ================== VENDAS ================== */}
          <h6 style={{ marginTop: 4, marginBottom: 8, fontSize: 13, color: "#7b809a" }}>
            Vendas do material (quantidades, nos últimos 3 anos)
          </h6>

          {vendasLoading ? (
            <div
              style={{
                padding: "16px 0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress size={24} />
            </div>
          ) : !temVendas ? (
            <div style={{ fontSize: 13, color: "#555", marginBottom: 16 }}>
              Não há vendas registadas para este material nos últimos 3 anos.
            </div>
          ) : (
            <div style={{ marginBottom: 20 }}>
              {/* Legenda das cores/anos */}
              <div
                style={{
                  fontSize: 12,
                  color: "#7b809a",
                  marginBottom: 8,
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 20,
                }}
              >
                {vendasDatasetsFiltrados.map((ds) => {
                  const total = vendasTotaisAno[ds.label] ?? 0;

                  return (
                    <span key={ds.label} style={{ display: "inline-flex", alignItems: "center" }}>
                      <span
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          marginRight: 6,
                          backgroundColor: colorMap[ds.color] || "#999",
                        }}
                      />
                      {ds.label}{" "}
                      <span style={{ color: "#3a3a3a", marginLeft: 4 }}>
                        (Qtd. total:
                        {Number(total).toLocaleString("pt-PT", {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        })}
                        )
                      </span>
                    </span>
                  );
                })}
              </div>

              <DefaultLineChart title="" description="" chart={vendasChart} />
            </div>
          )}

          {/* ================== VENDAS – FATURAS ================== */}
          <h6
            style={{
              marginTop: 4,
              marginBottom: 8,
              fontSize: 13,
              color: "#7b809a",
            }}
          >
            Vendas do material (faturas)
          </h6>

          {vendasLinhasLoading ? (
            <div
              style={{
                padding: "16px 0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress size={24} />
            </div>
          ) : vendasLinhasData.length === 0 ? (
            <div
              style={{
                padding: "8px 0",
                fontSize: 12,
                color: "#9e9e9e",
              }}
            >
              Sem faturas de venda para este material.
            </div>
          ) : (
            <div
              style={{
                maxHeight: 240,
                overflowY: "auto",
                marginBottom: 16,
              }}
            >
              <table
                {...getVendasLinhasTableProps()}
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: 13,
                }}
              >
                <thead>
                  {vendasLinhasHeaderGroups.map((headerGroup) => (
                    <tr key={headerGroup.id} {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map((column) => {
                        const widthStyle =
                          column.minWidth || column.maxWidth
                            ? {
                                ...(column.minWidth && {
                                  width: column.minWidth,
                                  minWidth: column.minWidth,
                                }),
                                ...(column.maxWidth && { maxWidth: column.maxWidth }),
                              }
                            : {};

                        return (
                          <th
                            key={column.id}
                            {...column.getHeaderProps()}
                            style={{
                              ...thStyle,
                              ...widthStyle,
                              textAlign: column.align || "left",
                              position: "sticky",
                              top: 0,
                              zIndex: 2,
                              backgroundColor: "#f8f9fa",
                            }}
                          >
                            <div>{column.render("Header")}</div>
                            {column.canFilter && (
                              <div style={{ marginTop: 4 }}>{column.render("Filter")}</div>
                            )}
                          </th>
                        );
                      })}
                    </tr>
                  ))}
                </thead>

                <tbody {...getVendasLinhasBodyProps()}>
                  {vendasLinhasRows.length === 0 && (
                    <tr>
                      <td
                        colSpan={vendasLinhasColumns.length}
                        style={{
                          ...tdStyle,
                          textAlign: "center",
                          color: "#9e9e9e",
                          padding: 14,
                        }}
                      >
                        Nenhum resultado encontrado para os filtros indicados.
                      </td>
                    </tr>
                  )}

                  {vendasLinhasRows.map((row) => {
                    prepareVendasLinhasRow(row);
                    return (
                      <tr key={row.id} {...row.getRowProps()}>
                        {row.cells.map((cell) => {
                          const col = cell.column;
                          const widthStyle =
                            col.minWidth || col.maxWidth
                              ? {
                                  ...(col.minWidth && {
                                    width: col.minWidth,
                                    minWidth: col.minWidth,
                                  }),
                                  ...(col.maxWidth && { maxWidth: col.maxWidth }),
                                }
                              : {};

                          return (
                            <td
                              key={col.id}
                              {...cell.getCellProps()}
                              style={{
                                ...tdStyle,
                                ...widthStyle,
                                textAlign: col.align || "left",
                              }}
                            >
                              {cell.render("Cell")}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {!podeVer ? null : (
            <>
              {/* ================== COMPRAS ================== */}
              <h6 style={{ marginTop: 4, marginBottom: 8, fontSize: 13, color: "#7b809a" }}>
                Compras do material (faturas)
              </h6>

              {comprasLoading ? (
                <div
                  style={{
                    padding: "16px 0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CircularProgress size={24} />
                </div>
              ) : comprasData.length === 0 ? (
                <div style={{ fontSize: 13, color: "#555" }}>
                  Não há registos de compras para este material.
                </div>
              ) : (
                <div style={{ maxHeight: 260, overflowY: "auto" }}>
                  <table
                    {...getComprasTableProps()}
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: 13,
                    }}
                  >
                    <thead>
                      {comprasHeaderGroups.map((hg) => (
                        <tr {...hg.getHeaderGroupProps()}>
                          {hg.headers.map((col) => {
                            const widthStyle =
                              col.minWidth || col.maxWidth
                                ? {
                                    ...(col.minWidth && {
                                      width: col.minWidth,
                                      minWidth: col.minWidth,
                                    }),
                                    ...(col.maxWidth && { maxWidth: col.maxWidth }),
                                  }
                                : {};

                            return (
                              <th
                                {...col.getHeaderProps()}
                                style={{
                                  ...thStyle,
                                  ...widthStyle,
                                  textAlign: col.align || "left",
                                  position: "sticky",
                                  top: 0,
                                  zIndex: 2,
                                  backgroundColor: "#f8f9fa",
                                }}
                              >
                                <div>{col.render("Header")}</div>
                                {col.canFilter && (
                                  <div style={{ marginTop: 4 }}>{col.render("Filter")}</div>
                                )}
                              </th>
                            );
                          })}
                        </tr>
                      ))}
                    </thead>

                    <tbody {...getComprasBodyProps()}>
                      {comprasRows.length === 0 && (
                        <tr>
                          <td
                            colSpan={comprasColumns.length}
                            style={{
                              ...tdStyle,
                              textAlign: "center",
                              color: "#9e9e9e",
                              padding: 14,
                            }}
                          >
                            Nenhum resultado encontrado para os filtros indicados.
                          </td>
                        </tr>
                      )}

                      {comprasRows.map((row) => {
                        prepareComprasRow(row);
                        return (
                          <tr {...row.getRowProps()}>
                            {row.cells.map((cell) => {
                              const col = cell.column;
                              const widthStyle =
                                col.minWidth || col.maxWidth
                                  ? {
                                      ...(col.minWidth && {
                                        width: col.minWidth,
                                        minWidth: col.minWidth,
                                      }),
                                      ...(col.maxWidth && {
                                        maxWidth: col.maxWidth,
                                      }),
                                    }
                                  : {};

                              return (
                                <td
                                  {...cell.getCellProps()}
                                  style={{
                                    ...tdStyle,
                                    ...widthStyle,
                                    textAlign: col.align || "left",
                                  }}
                                >
                                  {cell.render("Cell")}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <DialogActions>
            <MDButton color="info" variant="gradient" size="small" onClick={onClose}>
              Fechar
            </MDButton>
          </DialogActions>
        </DialogActions>
      </Dialog>

      <Dialog open={precosOpen} onClose={() => setPrecosOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Preços do material {precosRefMat ? `(${precosRefMat})` : ""}</DialogTitle>

        <DialogContent dividers>
          {precosLoading ? (
            <div style={{ padding: "24px 0", textAlign: "center" }}>
              <CircularProgress size={26} />
            </div>
          ) : !precos ? (
            <div style={{ fontSize: 13, color: "#555" }}>
              Não foram encontrados preços registados para este material.
            </div>
          ) : (
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 13,
                minWidth: 400,
              }}
            >
              <thead>
                <tr>
                  <th style={thStyle}>PV Acons.</th>
                  {podeVer && <th style={thStyle}>Margem</th>}
                  {podeVer && <th style={thStyle}>PC</th>}
                </tr>
              </thead>

              <tbody>
                <tr>
                  <td style={tdStyle}>{formatNum(precos.zpr0)}</td>
                  {podeVer && <td style={tdStyle}>{margemTexto}</td>}
                  {podeVer && <td style={tdStyle}>{formatNum(precos.zpvr)}</td>}
                </tr>
              </tbody>
            </table>
          )}
        </DialogContent>

        <DialogActions>
          <MDButton
            color="info"
            variant="gradient"
            size="small"
            onClick={() => setPrecosOpen(false)}
          >
            Fechar
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog open={stockDialogOpen} onClose={fecharAlterarStock} maxWidth="sm" fullWidth>
        <DialogTitle>Alterar Stock</DialogTitle>

        <DialogContent dividers>
          <div style={{ display: "grid", gap: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>
              {refMaterialAtual} — {material?.nome || ""}
            </div>

            <div style={{ fontSize: 12, color: "#7b809a" }}>
              Centro: <b>{stockSelecionado?.centro}</b> ({stockSelecionado?.refcentro})
            </div>

            <div>
              <div style={labelStyle}>Qtd oficial</div>
              <TextField
                size="small"
                fullWidth
                value={`${formatNum(stockSelecionado?.quantidade)} ${
                  stockSelecionado?.unidade || ""
                }`}
                disabled
              />
            </div>

            <div>
              <div style={labelStyle}>Qtd verificada</div>
              <TextField
                size="small"
                fullWidth
                type="number"
                inputProps={{
                  inputMode: "numeric",
                  step: "1",
                  min: "0",
                }}
                value={stockForm.qtd_verificada}
                onChange={(e) => setStockForm((p) => ({ ...p, qtd_verificada: e.target.value }))}
              />
            </div>

            <div>
              <div style={labelStyle}>Localização</div>
              <TextField
                size="small"
                fullWidth
                value={stockForm.localizacao}
                onChange={(e) =>
                  setStockForm((p) => ({
                    ...p,
                    localizacao: e.target.value.toUpperCase(),
                  }))
                }
              />
            </div>

            <div>
              <div style={labelStyle}>Observações</div>
              <TextField
                size="small"
                fullWidth
                multiline
                minRows={2}
                value={stockForm.obs}
                onChange={(e) => setStockForm((p) => ({ ...p, obs: e.target.value.toUpperCase() }))}
              />
            </div>
          </div>
        </DialogContent>

        <DialogActions>
          <MDButton color="secondary" onClick={fecharAlterarStock}>
            Cancelar
          </MDButton>
          <MDButton color="info" onClick={gravarAlteracaoStock}>
            Gravar
          </MDButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

// estilos reutilizáveis (iguais aos de Entidades)
const thStyle = {
  textAlign: "left",
  padding: "6px 8px",
  fontSize: 12,
  color: "#7b809a",
  borderBottom: "1px solid #eee",
  whiteSpace: "nowrap",
};

const tdStyle = {
  padding: "8px 10px",
  borderTop: "1px solid #eee",
  fontSize: 13,
};

const labelStyle = {
  fontSize: 12,
  color: "#7b809a",
  marginBottom: 4,
};

// filtro padrão
function DefaultColumnFilter({ column: { filterValue, setFilter } }) {
  return (
    <input
      value={filterValue || ""}
      onChange={(e) => setFilter(e.target.value || undefined)}
      style={{
        width: "100%",
        fontSize: 11,
        padding: "2px 4px",
        borderRadius: 4,
        border: "1px solid #ddd",
      }}
      placeholder="filtrar…"
    />
  );
}
